import axios from 'axios'
import { logger } from 'services/logger'
import {
  generateAIImage,
  getGenerationStatus,
  getPresignedURLForAIUpload,
  type AiGenJobResponseType,
  type PresignedURLType,
} from './api'
import { loadImage } from 'mixtiles-web-common/services/imageLoader'
import { AICreationError } from './consts'
import { getAmplitudeDeviceId } from 'services/Analytics/platforms/amplitude'
import { downscaleImageFile } from 'lib/image/utils/downscale'
import { getPhotoBankStore } from 'stores/RootStoreProvider'
import { type PhotoFaceData } from 'components/PhotoBank/PhotoBank.types'
import { UPLOAD_SRC } from 'components/PhotoBank/PhotoBank.consts'
import { type ExperimentManager } from 'services/ExperimentManager/ExperimentManager'

const POLLING_TIMEOUT = 7 * 60 * 1000 // 7 minutes
const POLLING_INTERVAL = 5 * 1000 // 5 seconds

const SOURCE_MAX_DIMENSION = 1024

async function uploadImagesToS3(imageFiles: File[]): Promise<string[]> {
  const presignedURLs = await getPresignedURLForAIUpload(imageFiles.length)

  async function handleUpload(presignedURL: PresignedURLType, imageFile: File) {
    const arrayBuffer = await imageFile.arrayBuffer()
    await axios.put(presignedURL.uploadURL, arrayBuffer, {
      headers: { 'Content-Type': imageFile.type },
    })
    return presignedURL.downloadURL
  }

  if (!Array.isArray(presignedURLs)) {
    const downloadURL = await handleUpload(presignedURLs, imageFiles[0])
    return [downloadURL]
  }

  return Promise.all(
    presignedURLs.map(async (presignedURL, index) => {
      return handleUpload(presignedURL, imageFiles[index])
    })
  )
}

export type AIArtworkGenerationResult = {
  jobId: string
  urls: AiGenJobResponseType['urls']
  width: number
  height: number
  totalDurationSeconds: number
  sourcesPerResult?: { uid: string; face?: PhotoFaceData }[][]
}

// Successful result slots paired with their original index, which stays aligned
// with the backend slots (for source-photo linkage and per-variant analytics).
export function getSuccessfulResults(urls: AiGenJobResponseType['urls']) {
  return urls
    .map((result, index) => ({ result, index }))
    .filter(({ result }) => result.status !== 'error')
}

export type VisibleResults = ReturnType<typeof getSuccessfulResults>

export async function pollJobStatusUntilDone(
  jobId: string,
  signal?: AbortSignal
): Promise<{
  urls: AiGenJobResponseType['urls']
  width: number
  height: number
}> {
  const startTime = Date.now()

  // eslint-disable-next-line no-constant-condition
  while (true) {
    signal?.throwIfAborted()

    const status = await getGenerationStatus(jobId)

    if (status.state === 'completed') {
      return { urls: status.urls, width: status.width, height: status.height }
    }

    if (status.state === 'error') {
      throw new AICreationError(status.ai_error_code)
    }

    // Checked only after a fresh status: a tab frozen in the background wakes past the limit with a job that may be done.
    if (Date.now() - startTime >= POLLING_TIMEOUT) {
      throw new Error('Polling timeout exceeded')
    }

    await new Promise<void>((resolve) => {
      const timer = setTimeout(resolve, POLLING_INTERVAL)
      signal?.addEventListener(
        'abort',
        () => {
          clearTimeout(timer)
          resolve()
        },
        { once: true }
      )
    })
  }
}

function durationInSeconds(duration: number): number {
  return Math.round(duration / 1000)
}

// One generation input: the source file + (for face-selection) the selected face within it.
export type SourceInput = { file: File; face?: PhotoFaceData }

// The uid is stable local->remote and available synchronously, so we DON'T await the S3 upload —
// we only need the uid for tile linkage. Skip linkage if any file was dropped (processing failure
// or content dedup), so we never mis-map a tile to the wrong source.
async function uploadSourcePhotos(files: File[]): Promise<Map<File, string>> {
  const photoBankStore = getPhotoBankStore()
  const { photos } = await photoBankStore.uploadPhotos({
    cloudServiceName: null,
    files,
    onUploadFailed: (uid) => photoBankStore.removePhoto(uid),
    uploadSrc: UPLOAD_SRC.AI_SOURCE,
    withFaceDetection: false,
    filterSmallFaces: false,
  })
  if (photos.length !== files.length) {
    throw new Error('Source photos upload dropped a file (count mismatch)')
  }
  return new Map(files.map((file, i) => [file, photos[i].uid]))
}

// Per input: its { uid, face }. Undefined (logged) if any uid mapping is missing.
function resolveSourcePerInput(
  fileToUid: Map<File, string>,
  sourceInputs: SourceInput[]
): { uid: string; face?: PhotoFaceData }[] | undefined {
  const perInput: { uid: string; face?: PhotoFaceData }[] = []
  for (const { file, face } of sourceInputs) {
    const uid = fileToUid.get(file)
    if (!uid) {
      logger.error(
        'Missing source uid for AI-gen tile linkage',
        new Error('source upload missing a file->uid mapping')
      )
      return undefined
    }
    perInput.push(face ? { uid, face } : { uid })
  }
  return perInput
}

export function shouldRouteTemplateToAiWorkflows(
  experimentManager: ExperimentManager,
  templateSlug: string
): boolean {
  if (!experimentManager.isEnabled('ai-templates-aiworkflows-routing')) {
    return false
  }

  return (
    experimentManager
      .getVariantPayload('ai-templates-aiworkflows-routing')
      ?.templateSlugs?.includes(templateSlug) ?? false
  )
}

export const generateAIArtwork = async ({
  templateSlug,
  imageFiles,
  sourceInputs,
  userText,
  variant,
  lowQuality,
  form,
  shouldUseAiWorkflows,
  productFlow,
  signal,
}: {
  templateSlug: string
  imageFiles?: File[]
  // One entry per generation input (source file + optional selected face). Defaults to
  // imageFiles for single-image flows; face-selection passes the originals + their faces.
  sourceInputs?: SourceInput[]
  userText?: string
  variant?: string
  lowQuality?: boolean
  form?: Record<string, string>
  shouldUseAiWorkflows?: boolean
  productFlow?: string
  signal?: AbortSignal
}): Promise<AIArtworkGenerationResult> => {
  const startTime = performance.now()
  try {
    let imageUrls: string[] | undefined
    let imageDimensions: [number, number][] | undefined

    const sourceInputList =
      sourceInputs ?? imageFiles?.map((file) => ({ file }))

    // Best-effort source upload for tile linkage — parallel, never awaited before generation.
    let sourcePhotosUploadPromise:
      | Promise<Map<File, string> | undefined>
      | undefined

    if (imageFiles?.length) {
      const uploadFiles = [
        ...new Set((sourceInputList ?? []).map((s) => s.file)),
      ]
      sourcePhotosUploadPromise = uploadSourcePhotos(uploadFiles).catch(
        (error) => {
          logger.error('Source photos upload failed', error)
          return undefined
        }
      )

      try {
        const downscaled = await Promise.all(
          imageFiles.map((file) =>
            downscaleImageFile(file, SOURCE_MAX_DIMENSION)
          )
        )
        imageDimensions = downscaled.map(
          ({ width, height }) => [width, height] as [number, number]
        )
        imageUrls = await uploadImagesToS3(downscaled.map(({ file }) => file))
      } catch (error) {
        logger.error('Image processing failed', error)
        throw new AICreationError('image_processing_failed')
      }
    }

    const { jobId } = await generateAIImage({
      variant,
      templateSlug,
      imageUrls,
      imageDimensions,
      userText,
      amplitudeDeviceId: getAmplitudeDeviceId(),
      lowQuality,
      form,
      useAiWorkflows: shouldUseAiWorkflows,
      productFlow,
    })
    const { urls, width, height } = await pollJobStatusUntilDone(jobId, signal)
    const failedUrls = urls.filter((url) => url.status === 'error')
    if (failedUrls.length === urls.length) {
      logger.error(
        'AI generation completed without successful urls',
        new Error('All AI generation result urls failed'),
        { templateSlug, failedCount: failedUrls.length }
      )
      throw new AICreationError(
        failedUrls[0]?.ai_error_code ?? 'unexpected_error'
      )
    }
    const totalDurationSeconds = durationInSeconds(
      performance.now() - startTime
    )

    // The uid map resolves fast (no S3 wait), so a plain await never blocks results.
    const fileToUid = sourcePhotosUploadPromise
      ? await sourcePhotosUploadPromise
      : undefined
    const perInput =
      fileToUid && sourceInputList
        ? resolveSourcePerInput(fileToUid, sourceInputList)
        : undefined
    // 1 result per input -> its own source; N inputs collapsed into 1 output -> all sources.
    const sourcesPerResult = perInput
      ? urls.map((_, i) =>
          urls.length === perInput.length ? [perInput[i]] : perInput
        )
      : undefined

    return {
      jobId,
      urls,
      width,
      height,
      totalDurationSeconds,
      sourcesPerResult,
    }
  } catch (error) {
    if (!(error instanceof DOMException && error.name === 'AbortError')) {
      logger.error('Error generating AI artwork', error, {
        templateSlug,
        totalDurationSeconds: durationInSeconds(performance.now() - startTime),
      })
    }
    throw error
  }
}

const TIMEOUT_MS = 10000
export const preloadPreviewImagesWithTimeout = async (
  results: string[]
): Promise<void> => {
  const loadPromises = results.map((result) => loadImage(result))

  await Promise.race([
    Promise.all(loadPromises),
    new Promise((resolve) => {
      setTimeout(() => resolve(null), TIMEOUT_MS)
    }),
  ])
}
